"use client";
import { Skeleton } from "@/components/ui/skeleton";
import { routeService } from "@/services/route";
import { useClerk, useSignIn } from "@clerk/nextjs";
import { isClerkAPIResponseError } from "@clerk/nextjs/errors";
import { ClerkAPIError, OAuthStrategy } from "@clerk/types";
import { useState } from "react";
import { GoogleButton, MicrosoftButton } from "../../oauth-buttons";

export function SignInForm({
  redirectUrl,
  clerkTicket,
}: {
  redirectUrl: string;
  clerkTicket?: string;
}) {
  const clerk = useClerk();
  const { signIn, isLoaded, setActive } = useSignIn();
  const [errors, setErrors] = useState<ClerkAPIError[]>();
  const [signingIn, setSigningIn] = useState<OAuthStrategy | null>(null);
  const [ticketTried, setTicketTried] = useState(false);

  if (!signIn && !isLoaded) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  }

  // GO-3516: an existing user's invite link (e.g. into a second org). Signing
  // in with the invitation's ticket accepts it; Clerk verified the email when
  // the link was opened. Returns false when the ticket doesn't finish sign-in.
  const signInWithTicket = async (ticket: string) => {
    const attempt = await signIn!.create({ strategy: "ticket", ticket });
    if (attempt.status !== "complete") {
      return false;
    }
    await setActive!({ session: attempt.createdSessionId });

    // Accepting the invitation created their newest membership: open that org,
    // landing as login would. Otherwise let them pick from their orgs.
    const [joined] = [...(clerk.user?.organizationMemberships ?? [])].sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    );
    if (joined) {
      await setActive!({ organization: joined.organization.id });
    }
    window.location.assign(
      routeService.getUrl(joined ? "root" : "org-root"),
    );
    return true;
  };

  const onSignIn = async (strategy: OAuthStrategy) => {
    setSigningIn(strategy);
    setErrors(undefined);

    if (!isLoaded) {
      return;
    }

    try {
      // Try the ticket once. If it's expired, revoked or already used, the
      // error is shown and the next click signs in normally.
      if (clerkTicket && !ticketTried) {
        setTicketTried(true);
        if (await signInWithTicket(clerkTicket)) {
          return;
        }
      }

      // const signInAttempt = await signIn.create({
      //   strategy,
      //   redirectUrl: routeService.getUrl("sso-callback"),
      // });

      const attempt = await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: routeService.getUrl("sso-callback"),
        redirectUrlComplete: redirectUrl
          ? redirectUrl
          : routeService.getUrl("root"),
      });
    } catch (e) {
      if (isClerkAPIResponseError(e)) {
        setErrors(e.errors);
      }
    } finally {
      setSigningIn(null);
    }
  };

  return (
    <div className="space-y-4">
      <GoogleButton onClick={onSignIn} loading={signingIn === "oauth_google"}>
        Sign in with Google
      </GoogleButton>
      <MicrosoftButton
        onClick={onSignIn}
        loading={signingIn === "oauth_microsoft"}
      >
        Sign in with Microsoft
      </MicrosoftButton>
      {errors && (
        <div className="text-red-500">
          {errors.map((error) => error.longMessage).join(", ")}
        </div>
      )}
    </div>
  );
}
